import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  ButtonBase,
  Chip,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useTranslation } from 'react-i18next';

// Regions where Advanced tier is available (add 'eu' to enable for Europe)
export const ADVANCED_TIER_REGIONS = ['ua', 'asia', 'other'];

// Base pricing data for tiers by region (before subscription bonus)
const TIER_DATA = {
  ua: {
    symbol: '$',
    basic: { price: 11 },
    pro: { price: 22 },
    corporate: { price: 34 },
    advanced: { price: 22 },
  },
  eu: {
    symbol: '€',
    basic: { price: 20 },
    pro: { price: 34 },
    corporate: { price: 49 },
    advanced: { price: 34 },
  },
  asia: {
    symbol: '$',
    basic: { price: 13.2 },
    pro: { price: 24 },
    corporate: { price: 37 },
    advanced: { price: 24 },
  },
  other: {
    symbol: '$',
    basic: { price: 20 },
    pro: { price: 34 },
    corporate: { price: 49 },
    advanced: { price: 34 },
  },
};

// Default subscription bonus discount factor (20% bonus = divide by 1.2)
const SUBSCRIPTION_BONUS_FACTOR = 1.2;

const BASE_TIERS = [
  { id: 'basic', name: 'Basic' },
  { id: 'pro', name: 'Pro' },
  { id: 'corporate', name: 'Corp' },
];

/**
 * TierSelector - Compact segmented control for Business Phone tier selection
 */
const TierSelector = ({ selected, onChange, region, onCompareClick, showAdvancedTier }) => {
  const { t } = useTranslation();

  const regionData = TIER_DATA[region] || TIER_DATA.other;
  const { symbol } = regionData;

  // Build tiers list: insert Advanced between Pro and Corporate when enabled for this region
  const tiers = useMemo(() => {
    if (showAdvancedTier && ADVANCED_TIER_REGIONS.includes(region)) {
      return [
        { id: 'basic', name: 'Basic' },
        { id: 'pro', name: 'Pro' },
        { id: 'advanced', name: 'Advanced' },
        { id: 'corporate', name: 'Corp' },
      ];
    }
    return BASE_TIERS;
  }, [showAdvancedTier, region]);

  return (
    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* Tier Buttons Container */}
      <Box
        sx={{
          display: 'inline-flex',
          bgcolor: '#f3f4f6',
          borderRadius: 1,
          p: 0.375,
          gap: 0.375,
        }}
      >
        {tiers.map((tier) => {
          const isSelected = selected === tier.id;
          const isPro = tier.id === 'pro';

          return (
            <ButtonBase
              key={tier.id}
              onClick={() => onChange(tier.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                px: 2,
                minWidth: 70,
                borderRadius: 0.75,
                transition: 'all 0.2s ease',
                bgcolor: isSelected ? '#fff' : 'transparent',
                boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                '&:hover': {
                  bgcolor: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: '0.875rem',
                  color: isSelected ? '#00C853' : '#666',
                }}
              >
                {tier.name}
              </Typography>
              {isPro && (
                <Chip
                  label="Best"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    bgcolor: '#FF9800',
                    color: '#fff',
                    '& .MuiChip-label': {
                      px: 0.75,
                    },
                  }}
                />
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {/* Compare link */}
      {onCompareClick && (
        <ButtonBase
          onClick={onCompareClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#666',
            '&:hover': { color: '#333' }
          }}
        >
          <CompareArrowsIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.875rem' }}>
            {t('calculator.compareFeatures', 'Compare features')}
          </Typography>
        </ButtonBase>
      )}
    </Box>
  );
};

export default TierSelector;
